import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { can } from '../utils/roles';
import {
  Plus, Search, Phone, Edit2, Trash2, X, PhoneCall,
  Upload, Users, StickyNote, RefreshCw, Mail, Tag, DollarSign
} from 'lucide-react';

const STATUS_OPTIONS = ['new', 'contacted', 'interested', 'not_interested', 'callback', 'not_picked', 'won', 'lost'];
const STATUS_COLORS = {
  new: 'bg-blue-500/10 text-blue-400',
  contacted: 'bg-cyan-500/10 text-cyan-400',
  interested: 'bg-purple-500/10 text-purple-400',
  not_interested: 'bg-red-500/10 text-red-400',
  callback: 'bg-orange-500/10 text-orange-400',
  not_picked: 'bg-yellow-500/10 text-yellow-400',
  won: 'bg-green-500/10 text-green-400',
  lost: 'bg-dark-500/10 text-dark-400',
};

export default function Leads() {
  const { user } = useAuth();
  const isAdmin = can.viewAllLeads(user?.role);
  const isManager = user?.role === 'manager';
  const canUpload = can.uploadBulk(user?.role);
  const canAssign = can.assignLeads(user?.role);
  const canSoftDelete = can.softDeleteLead(user?.role);
  const canPermDelete = can.permDeleteLead(user?.role);
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [undoCount, setUndoCount] = useState(0);

  const saveForUndo = (lead) => {
    if (!window.__undoStack) window.__undoStack = [];
    window.__undoStack = [...window.__undoStack.slice(-9), {
      id: lead.id, name: lead.name,
      status: lead.status,
      assigned_agent_id: lead.assigned_agent_id,
      is_deleted: lead.is_deleted
    }];
    setUndoCount(window.__undoStack.length);
  };

  const handleUndo = async () => {
    const stack = window.__undoStack || [];
    if (stack.length === 0) { alert('Nothing to undo in this session'); return; }
    const prev = stack[stack.length - 1];
    try {
      if (prev.is_deleted === false) {
        try { await api.post(`/api/leads/${prev.id}/restore`); } catch(e) {}
      }
      await api.put(`/api/leads/${prev.id}`, {
        status: prev.status,
        assigned_agent_id: prev.assigned_agent_id || null
      });
      window.__undoStack = stack.slice(0, -1);
      setUndoCount(window.__undoStack.length);
      loadLeads();
    } catch(e) {
      alert('Undo failed: ' + (e.response?.data?.detail || e.message));
    }
  };
  const [uploadCountry, setUploadCountry] = useState('');
  const [uploadGroupName, setUploadGroupName] = useState('');
  const [uploadBatchSize, setUploadBatchSize] = useState(200);
  const [showDetail, setShowDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showNotes, setShowNotes] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', source: '', status: 'new',
    assigned_agent_id: '', notes: '', property_interest: '', property_interest_id: '', budget: '', occupation: ''
  });
  const [noteForm, setNoteForm] = useState({ content: '', meeting_date: '', reminder_days: '' });
  const [assignForm, setAssignForm] = useState({ mode: 'selective', agent_ids: [], count_per_agent: 5 });

  const [agentFilter, setAgentFilter] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [occupationFilter, setOccupationFilter] = useState('');
  const [occupations, setOccupations] = useState([]);
  const [groupFilter, setGroupFilter] = useState('');
  const [groups, setGroups] = useState([]);

  const loadLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (assignedFilter === 'assigned') params.set('assigned', 'true');
      if (assignedFilter === 'unassigned') params.set('assigned', 'false');
      if (showArchived) params.set('include_deleted', 'true');
      if (agentFilter) params.set('agent_id', agentFilter);
      if (occupationFilter) params.set('occupation', occupationFilter);
      if (groupFilter) params.set('group_id', groupFilter);
      if (sortBy) params.set('sort_by', sortBy);
      params.set('limit', pageSize);
      params.set('skip', currentPage * pageSize);
      const res = await api.get(`/api/leads?${params}`);
      setLeads(res.data.leads);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, statusFilter, assignedFilter, showArchived, agentFilter, occupationFilter, groupFilter, sortBy, pageSize, currentPage]);

  useEffect(() => {
    loadLeads();
    if (isAdmin || isManager) api.get('/api/agents').then(res => setAgents(res.data.agents)).catch(e => console.log('agents error', e));
  }, [loadLeads, isAdmin, isManager]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLead) {
        saveForUndo(editingLead);
        await api.put(`/api/leads/${editingLead.id}`, formData);
      } else {
        await api.post('/api/leads', formData);
      }
      setShowForm(false); setEditingLead(null); resetForm(); loadLeads();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Archive this lead?')) return;
    try {
      const lead = leads.find(l => l.id === id);
      if (lead) saveForUndo({...lead, is_deleted: false});
      await api.delete(`/api/leads/${id}`);
      loadLeads();
    } catch (err) { alert('Error'); }
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name || '', phone: lead.phone || '', email: lead.email || '',
      source: lead.source || '', status: lead.status || 'new',
      assigned_agent_id: lead.assigned_agent_id || '', notes: lead.notes || '',
      property_interest: lead.property_interest || '', property_interest_id: lead.property_interest_id || '', budget: lead.budget || ''
    });
    setShowForm(true);
  };

  const handleCall = async (phone, leadId) => {
    // Normal agents: open phone dialer directly
    if (user?.agent_type === 'normal_agent' || (user?.role !== 'admin' && user?.role !== 'manager' && user?.agent_type !== 'voip_agent')) {
      window.open('tel:' + phone, '_self');
      return;
    }
    // VoIP agents and admin: use Twilio bridge
    if (true) {
      try {
        const res = await api.post('/api/calls/outbound', { phone_number: phone, lead_id: leadId, agent_id: user.id });
        alert(res.data.message || 'Call initiated!');
      } catch(err) { alert(err.response?.data?.detail || 'Error making call'); }
    }
  };
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      let params = []; if(uploadCountry) params.push('default_country='+uploadCountry); if(uploadGroupName) params.push('group_name='+encodeURIComponent(uploadGroupName)); params.push('batch_size='+uploadBatchSize); const url = '/api/leads/upload' + (params.length ? '?'+params.join('&') : '');
      const res = await api.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert(res.data.message);
      setShowUpload(false);
      loadLeads();
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
  };

  const handleAssign = async () => {
    try {
      // Save current state for undo
      selectedLeads.forEach(id => {
        const lead = leads.find(l => l.id === id);
        if (lead) saveForUndo(lead);
      });
      const body = {
        lead_ids: selectedLeads,
        agent_ids: assignForm.agent_ids,
        mode: assignForm.mode,
        count_per_agent: assignForm.count_per_agent
      };
      const res = await api.post('/api/leads/assign', body);
      alert(res.data.message);
      setShowAssign(false);
      setSelectedLeads([]);
      loadLeads();
    } catch (err) { alert(err.response?.data?.detail || 'Assignment failed'); }
  };

  const handleBulkUnassign = async () => {
    if (!window.confirm(`Unassign ${selectedLeads.length} leads back to system?`)) return;
    try {
      selectedLeads.forEach(id => {
        const lead = leads.find(l => l.id === id);
        if (lead) saveForUndo(lead);
      });
      await Promise.all(selectedLeads.map(id => api.put(`/api/leads/${id}`, { assigned_agent_id: null })));
      setSelectedLeads([]);
      loadLeads();
    } catch(e) { alert('Error unassigning leads'); }
  };

  const handleRechurn = async () => {
    if (!window.confirm('Process re-churn for all eligible leads?')) return;
    try {
      const res = await api.post('/api/leads/process-rechurn');
      alert(res.data.message);
      loadLeads();
    } catch (err) { alert(err.response?.data?.detail || 'Re-churn failed'); }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!showNotes) return;
    try {
      const data = { ...noteForm, reminder_days: noteForm.reminder_days ? parseInt(noteForm.reminder_days) : null };
      await api.post(`/api/leads/${showNotes.id}/notes`, data);
      setNoteForm({ content: '', meeting_date: '', reminder_days: '' });
      // Reload lead
      const res = await api.get(`/api/leads/${showNotes.id}`);
      setShowNotes(res.data);
    } catch (err) { alert('Error adding note'); }
  };

  const toggleSelect = (id) => {
    setSelectedLeads(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedLeads.length === leads.length) setSelectedLeads([]);
    else setSelectedLeads(leads.map(l => l.id));
  };

  const handleBulkStatusChange = async (status) => {
    if (!status) return;
    if (!window.confirm(`Change ${selectedLeads.length} leads to '${status}'?`)) return;
    try {
      selectedLeads.forEach(id => {
        const lead = leads.find(l => l.id === id);
        if (lead) saveForUndo(lead);
      });
      await Promise.all(selectedLeads.map(id => api.put(`/api/leads/${id}`, { status })));
      setSelectedLeads([]);
      loadLeads();
    } catch(e) { alert('Error updating status'); }
  };

  const handleBulkDelete = async () => {
    const action = showArchived ? 'Permanently delete' : 'Archive';
    if (!window.confirm(`${action} ${selectedLeads.length} selected leads?`)) return;
    try {
      await Promise.all(selectedLeads.map(id =>
        showArchived ? api.delete(`/api/leads/${id}?permanent=true`) : api.delete(`/api/leads/${id}`)
      ));
      setSelectedLeads([]);
      loadLeads();
    } catch (err) { alert('Error'); }
  };
  const handleRestore = async (id) => {
    try {
      await api.post(`/api/leads/${id}/restore`);
      loadLeads();
    } catch (err) { alert('Error restoring lead'); }
  };
  const resetForm = () => setFormData({
    name: '', phone: '', email: '', source: '', status: 'new',
    assigned_agent_id: '', notes: '', property_interest: '', property_interest_id: '', budget: '', occupation: ''
  });

  useEffect(() => {
    api.get('/api/v2/property-tree').then(r => setPropertyTree(r.data.tree || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/api/leads/occupations').then(r => setOccupations(r.data.occupations || [])).catch(() => {});
    api.get('/api/groups').then(r => setGroups(r.data.groups || [])).catch(() => {});
  }, []);

  const openDetail = async (lead) => {
    setShowDetail(lead);
    setTimeline(null);
    setTimelineLoading(true);
    try {
      const res = await api.get(`/api/leads/${lead.id}/timeline`);
      setTimeline(res.data);
    } catch (e) { setTimeline(null); }
    setTimelineLoading(false);
  };

  const getAssignmentTimer = (lead) => {
    if (!lead.assigned_agent_id || !lead.assignment_history?.length) return null;
    const last = lead.assignment_history[lead.assignment_history.length - 1];
    if (!last?.assigned_at) return null;
    const diffMs = Date.now() - new Date(last.assigned_at).getTime();
    const diffHours = diffMs / 3600000;
    const diffDays = diffHours / 24;
    let color, text;
    if (diffHours < 24) {
      color = 'bg-green-500/10 text-green-400';
      text = `${Math.floor(diffHours)}h`;
    } else if (diffHours < 72) {
      color = 'bg-yellow-500/10 text-yellow-400';
      text = `${Math.floor(diffHours)}h`;
    } else if (diffDays < 7) {
      color = 'bg-red-500/10 text-red-400';
      text = `${Math.floor(diffDays)}d`;
    } else if (diffDays < 30) {
      color = 'bg-red-500/10 text-red-400';
      text = `${Math.floor(diffDays / 7)}w`;
    } else {
      color = 'bg-red-500/10 text-red-400';
      text = `${Math.floor(diffDays / 30)}mo`;
    }
    return { color, text };
  };

  // Agent search filter
  const [propertyTree, setPropertyTree] = useState([]);
  const [selDev, setSelDev] = useState('');
  const [selProject, setSelProject] = useState('');
  const [selUnit, setSelUnit] = useState('');
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const filteredAgents = agents.filter(a =>
    a.name?.toLowerCase().includes(agentSearch.toLowerCase()) ||
    a.username?.toLowerCase().includes(agentSearch.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-xl font-bold text-white">Leads ({total})</h3>
          <p className="text-dark-400 text-sm">Manage sales leads</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={handleUndo} disabled={undoCount === 0}
            className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm transition-colors ${undoCount > 0 ? 'bg-dark-700 text-dark-300 hover:bg-dark-600 cursor-pointer' : 'bg-dark-800 text-dark-600 cursor-not-allowed'}`}
            title={undoCount > 0 ? `Undo last action` : 'Nothing to undo'}>
            ↩ Undo {undoCount > 0 ? `(${undoCount})` : ''}
          </button>
          {canUpload && <button onClick={() => setShowUpload(true)} className="flex items-center gap-1 px-3 py-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded-lg text-sm"><Upload className="w-4 h-4" />Upload</button>}
          {canAssign && selectedLeads.length > 0 && <button onClick={() => setShowAssign(true)} className="flex items-center gap-1 px-3 py-2 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 rounded-lg text-sm"><Users className="w-4 h-4" />Assign ({selectedLeads.length})</button>}
          {canAssign && selectedLeads.length > 0 && <button onClick={handleBulkUnassign} className="flex items-center gap-1 px-3 py-2 bg-dark-700 text-dark-300 hover:bg-dark-600 rounded-lg text-sm">↩ Unassign ({selectedLeads.length})</button>}
          {selectedLeads.length > 0 && (
            <select onChange={e => { handleBulkStatusChange(e.target.value); e.target.value=''; }} className="px-3 py-2 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-lg text-sm">
              <option value="">Change Status ({selectedLeads.length})</option>
              {STATUS_OPTIONS.map(s =>
                <option key={s} value={s}>{s.replace(/_/g,' ').replace(/\w/g,c=>c.toUpperCase())}</option>
              )}
            </select>
          )}
          {selectedLeads.length > 0 && <button onClick={handleBulkDelete} className="flex items-center gap-1 px-3 py-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg text-sm"><Trash2 className="w-4 h-4" />Delete ({selectedLeads.length})</button>}
          {canUpload && <button onClick={handleRechurn} className="flex items-center gap-1 px-3 py-2 bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 rounded-lg text-sm"><RefreshCw className="w-4 h-4" />Re-Churn</button>}
          <button onClick={() => { setShowArchived(!showArchived); setSelectedLeads([]); }} className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm ${showArchived ? 'bg-red-500/20 text-red-400' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'}`}><Trash2 className="w-4 h-4" />{showArchived ? 'Archived' : 'Archive'}</button>
          {!showArchived && <button onClick={() => { setEditingLead(null); resetForm(); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-900 font-medium rounded-lg text-sm"><Plus className="w-4 h-4" />Add Lead</button>}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" /><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search leads..." className="w-full pl-10 pr-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-500 text-sm focus:outline-none focus:border-primary-500" /></div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
        </select>
        <select value={assignedFilter} onChange={e => setAssignedFilter(e.target.value)} className="px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
          <option value="">All Leads</option>
          <option value="assigned">Assigned</option>
          <option value="unassigned">Unassigned</option>
        </select>
        {(isAdmin || isManager) && (
          <div className="relative">
            <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)} className="px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
              <option value="">All Agents</option>
              <option value="__search__" disabled>── type to search ──</option>
              {filteredAgents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        )}
        <input type="text" placeholder="🔍 Agent name..." value={agentSearch} onChange={e => setAgentSearch(e.target.value)} className="px-3 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm w-36 placeholder-dark-500 focus:outline-none focus:border-primary-500" />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
          <option value="">Sort: Latest</option>
          <option value="contact_count">Sort: Most Contacted</option>
        </select>
        <select value={occupationFilter} onChange={e => { setOccupationFilter(e.target.value); setCurrentPage(0); }} className="px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
          <option value="">All Occupations</option>
          {occupations.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={groupFilter} onChange={e => { setGroupFilter(e.target.value); setCurrentPage(0); }} className="px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
          <option value="">All Groups</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-dark-400">
          <span>Show:</span>
          {[10, 50, 100, 200].map(s => (
            <button key={s} onClick={() => { setPageSize(s); setCurrentPage(0); }}
              className={`px-3 py-1 rounded text-xs ${pageSize === s ? 'bg-primary-500 text-dark-900 font-medium' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'}`}>{s}</button>
          ))}
          <span className="ml-2 text-dark-400">{total} total</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCurrentPage(p => Math.max(0, p-1))} disabled={currentPage === 0}
            className="px-3 py-1.5 bg-dark-700 text-dark-300 rounded text-sm disabled:opacity-40 hover:bg-dark-600">← Prev</button>
          <span className="text-dark-400 text-sm">Page {currentPage + 1} of {Math.ceil(total / pageSize) || 1}</span>
          <button onClick={() => setCurrentPage(p => p + 1)} disabled={(currentPage + 1) * pageSize >= total}
            className="px-3 py-1.5 bg-dark-700 text-dark-300 rounded text-sm disabled:opacity-40 hover:bg-dark-600">Next →</button>
        </div>
      </div>

      {/* Lead List */}
      {loading ? <div className="flex justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div></div> :
      leads.length === 0 ? <div className="glass rounded-xl p-8 text-center"><p className="text-dark-400">No leads found</p></div> :
      <div className="space-y-2">
        {isAdmin && <div className="flex items-center gap-2 px-2"><input type="checkbox" checked={selectedLeads.length === leads.length && leads.length > 0} onChange={toggleSelectAll} className="rounded" /><span className="text-dark-400 text-xs">Select All</span></div>}
        {leads.map(lead => (
          <div key={lead.id} className="glass rounded-xl p-4 hover:border-dark-500 transition-colors cursor-pointer" onClick={() => openDetail(lead)}>
            <div className="flex items-start gap-3">
              {isAdmin && <input type="checkbox" checked={selectedLeads.includes(lead.id)} onChange={() => toggleSelect(lead.id)} onClick={e => e.stopPropagation()} className="mt-1 rounded" />}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-white font-medium">{lead.name}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[lead.status] || ''}`}>{lead.status?.replace('_', ' ')}</span>
                      {lead.churn_status && <span className="px-2 py-0.5 rounded text-xs bg-orange-500/10 text-orange-400">{lead.churn_status}</span>}
                      {lead.assigned_agent_id ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-green-500/10 text-green-400">
                          {agents.find(a => a.id === lead.assigned_agent_id)?.name || 'assigned'}
                        </span>
                      ) : <span className="px-2 py-0.5 rounded text-xs bg-dark-600 text-dark-400">unassigned</span>}
                      {(() => { const t = getAssignmentTimer(lead); return t ? <span className={`px-2 py-0.5 rounded text-xs font-mono ${t.color}`}>{t.text}</span> : null; })()}
                      {(lead.contact_count > 0) && <span className="px-2 py-0.5 rounded text-xs bg-blue-500/10 text-blue-400">📞 {lead.contact_count}x</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-dark-400">
                      {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
                      {lead.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>}
                      {lead.source && <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{lead.source}</span>}
                      {lead.occupation && <span className="flex items-center gap-1 text-cyan-400"><span className="text-xs">💼</span>{lead.occupation}</span>}
                      {lead.budget && <span className="flex items-center gap-1"><span className="text-xs font-medium text-dark-400">AED</span>&nbsp;{lead.budget}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setShowNotes(lead)} className="p-1.5 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 rounded" title="Notes"><StickyNote className="w-4 h-4" /></button>
                    {lead.phone && <button onClick={() => handleCall(lead.phone, lead.id)} className="p-1.5 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded" title="Call"><PhoneCall className="w-4 h-4" /></button>}
                    <button onClick={() => handleEdit(lead)} className="p-1.5 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded" title="Edit"><Edit2 className="w-4 h-4" /></button>
                    {canSoftDelete && showArchived ? (
                      <>
                        <button onClick={() => handleRestore(lead.id)} className="p-1.5 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded" title="Restore">↩</button>
                        <button onClick={async () => {
                          if (!window.confirm('Permanently delete this lead? Cannot be undone.')) return;
                          await api.delete(`/api/leads/${lead.id}?permanent=true`);
                          loadLeads();
                        }} className="p-1.5 bg-red-700/20 text-red-500 hover:bg-red-700/30 rounded" title="Permanently Delete">🗑</button>
                      </>
                    ) : (
                      canSoftDelete && <button onClick={() => handleDelete(lead.id)} className="p-1.5 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded" title="Archive"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>}

      {/* Create/Edit Lead Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-dark-900 border border-dark-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-dark-700">
              <h3 className="text-lg font-semibold text-white">{editingLead ? 'Edit Lead' : 'Add Lead'}</h3>
              <button onClick={() => { setShowForm(false); setEditingLead(null); }} className="p-1 text-dark-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm text-dark-300 mb-1">Name *</label><input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" /></div>
                <div><label className="block text-sm text-dark-300 mb-1">Phone <span className="text-red-400">*required</span></label><input type="text" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" /></div>
                <div><label className="block text-sm text-dark-300 mb-1">Email</label><input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" /></div>
                <div><label className="block text-sm text-dark-300 mb-1">Source</label><input type="text" value={formData.source} onChange={e => setFormData({...formData, source: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" /></div>
                <div>
                  <label className="block text-sm text-dark-300 mb-1">Property Interest</label>
                  <div className="space-y-2">
                    <select value={selDev} onChange={e => { setSelDev(e.target.value); setSelProject(''); setSelUnit(''); }} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
                      <option value="">-- Select Developer --</option>
                      {propertyTree.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                    {selDev && (
                      <select value={selProject} onChange={e => { setSelProject(e.target.value); setSelUnit(''); }} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
                        <option value="">-- Select Project --</option>
                        {propertyTree.find(d=>d.id===selDev)?.projects?.map(p => <option key={p.id} value={p.id}>{p.name} {p.location ? '('+p.location+')' : ''}</option>)}
                      </select>
                    )}
                    {selProject && (
                      <select value={selUnit} onChange={e => {
                        setSelUnit(e.target.value);
                        const dev = propertyTree.find(d=>d.id===selDev);
                        const proj = dev?.projects?.find(p=>p.id===selProject);
                        const unit = proj?.unit_types?.find(u=>u.id===e.target.value);
                        if (unit) {
                          setFormData({...formData,
                            property_interest: dev.name+' - '+proj.name+' - '+unit.bedroom+' '+unit.unit_type,
                            property_interest_id: unit.id
                          });
                        } else if (e.target.value === 'not_finalised') {
                          setFormData({...formData, property_interest: 'Not Finalised', property_interest_id: ''});
                        }
                      }} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
                        <option value="">-- Select Unit Type --</option>
                        <option value="not_finalised">Not Finalised</option>
                        {propertyTree.find(d=>d.id===selDev)?.projects?.find(p=>p.id===selProject)?.unit_types?.map(u => (
                          <option key={u.id} value={u.id}>{u.bedroom} {u.unit_type} {u.area_sqft ? '('+u.area_sqft+'sqft)' : ''} {u.price_aed ? '- AED '+Number(u.price_aed).toLocaleString() : ''}</option>
                        ))}
                      </select>
                    )}
                    {formData.property_interest && <p className="text-primary-400 text-xs">Selected: {formData.property_interest}</p>}
                  </div>
                </div>
                <div><label className="block text-sm text-dark-300 mb-1">Occupation / Profession</label><input type="text" value={formData.occupation} onChange={e => setFormData({...formData, occupation: e.target.value})} placeholder="e.g. Business Owner, Doctor" className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" /></div>
                <div><label className="block text-sm text-dark-300 mb-1">Budget (AED)</label><input type="text" value={formData.budget} onChange={e => setFormData({...formData, budget: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm text-dark-300 mb-1">Status</label><select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">{STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}</select></div>
                {isAdmin && agents.length > 0 && <div><label className="block text-sm text-dark-300 mb-1">Assign Agent</label><select value={formData.assigned_agent_id} onChange={e => setFormData({...formData, assigned_agent_id: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm"><option value="">Unassigned</option>{agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>}
              </div>
              <div><label className="block text-sm text-dark-300 mb-1">Notes</label><textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm resize-none h-16" /></div>
              <div className="flex gap-3"><button type="button" onClick={() => { setShowForm(false); setEditingLead(null); }} className="flex-1 py-2.5 bg-dark-800 text-dark-300 rounded-lg text-sm">Cancel</button><button type="submit" className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-dark-900 rounded-lg text-sm font-medium">{editingLead ? 'Update' : 'Create'}</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-dark-900 border border-dark-700 rounded-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Upload Leads</h3>
            <p className="text-dark-400 text-sm mb-4">Supports CSV, Excel (.xlsx), and PDF files. Columns are auto-detected.</p>
            <div className="mb-3">
              <label className="block text-xs text-dark-400 mb-1">Group Name <span className="text-dark-600">(optional — organises leads into batches)</span></label>
              <input value={uploadGroupName} onChange={e => setUploadGroupName(e.target.value)} placeholder="e.g. Godrej One May 2026" className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500" />
            </div>
            <div className="mb-3">
              <label className="block text-xs text-dark-400 mb-1">Batch Size <span className="text-dark-600">(leads per batch)</span></label>
              <input type="number" value={uploadBatchSize} onChange={e => setUploadBatchSize(parseInt(e.target.value)||200)} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500" />
            </div>
            <div className="mb-4">
              <label className="block text-xs text-dark-400 mb-1">Default Country <span className="text-dark-600">(optional — helps format phone numbers)</span></label>
              <select value={uploadCountry} onChange={e => setUploadCountry(e.target.value)} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
                <option value="">Auto-detect country</option>
                <option value="IN">🇮🇳 India (+91)</option>
                <option value="AE">🇦🇪 UAE (+971)</option>
                <option value="GB">🇬🇧 UK (+44)</option>
                <option value="US">🇺🇸 USA (+1)</option>
                <option value="SA">🇸🇦 Saudi Arabia (+966)</option>
                <option value="PK">🇵🇰 Pakistan (+92)</option>
                <option value="QA">🇶🇦 Qatar (+974)</option>
                <option value="SG">🇸🇬 Singapore (+65)</option>
                <option value="AU">🇦🇺 Australia (+61)</option>
              </select>
            </div>
            <input type="file" accept=".csv,.xlsx,.xls,.pdf" onChange={handleUpload} className="w-full text-dark-300 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-500 file:text-dark-900 file:font-medium file:cursor-pointer" />
            <button onClick={() => setShowUpload(false)} className="mt-4 w-full py-2 bg-dark-800 text-dark-300 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-1">
        <div className="flex items-center gap-2 text-sm text-dark-400">
          <span>Show:</span>
          {[10, 50, 100, 200].map(s => (
            <button key={s} onClick={() => { setPageSize(s); setCurrentPage(0); }}
              className={`px-3 py-1 rounded text-xs ${pageSize === s ? 'bg-primary-500 text-dark-900 font-medium' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'}`}>
              {s}
            </button>
          ))}
          <span className="ml-2">{total} total</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCurrentPage(p => Math.max(0, p-1))} disabled={currentPage === 0}
            className="px-3 py-1.5 bg-dark-700 text-dark-300 rounded text-sm disabled:opacity-40 hover:bg-dark-600">
            ← Prev
          </button>
          <span className="text-dark-400 text-sm">Page {currentPage + 1} of {Math.ceil(total / pageSize) || 1}</span>
          <button onClick={() => setCurrentPage(p => p + 1)} disabled={(currentPage + 1) * pageSize >= total}
            className="px-3 py-1.5 bg-dark-700 text-dark-300 rounded text-sm disabled:opacity-40 hover:bg-dark-600">
            Next →
          </button>
        </div>
      </div>

      {/* Assign Modal */}
      {showAssign && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-dark-900 border border-dark-700 rounded-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Assign Leads ({selectedLeads.length} selected)</h3>
            <div className="space-y-4">
              <div><label className="block text-sm text-dark-300 mb-1">Mode</label><select value={assignForm.mode} onChange={e => setAssignForm({...assignForm, mode: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
                <option value="selective">Selective (round-robin selected)</option><option value="batch">Batch (equal distribution)</option><option value="random_selected">Random to Selected Agents</option><option value="fully_random">Fully Random (all active)</option>
              </select></div>
              {assignForm.mode !== 'fully_random' && (
                <div><label className="block text-sm text-dark-300 mb-1">Select Agents</label>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {agents.map(a => (
                      <label key={a.id} className="flex items-center gap-2 p-2 bg-dark-800/50 rounded text-sm text-white cursor-pointer">
                        <input type="checkbox" checked={assignForm.agent_ids.includes(a.id)} onChange={() => {
                          const ids = assignForm.agent_ids.includes(a.id) ? assignForm.agent_ids.filter(x => x !== a.id) : [...assignForm.agent_ids, a.id];
                          setAssignForm({...assignForm, agent_ids: ids});
                        }} />
                        {a.name} ({a.role})
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => setShowAssign(false)} className="flex-1 py-2.5 bg-dark-800 text-dark-300 rounded-lg text-sm">Cancel</button>
                <button onClick={handleAssign} className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-dark-900 rounded-lg text-sm font-medium">Assign</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes Drawer */}
      {showNotes && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-dark-900 border border-dark-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-dark-700 sticky top-0 bg-dark-900 z-10">
              <h3 className="text-lg font-semibold text-white">Notes: {showNotes.name}</h3>
              <button onClick={() => setShowNotes(null)} className="p-1 text-dark-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Existing notes */}
              {(showNotes.client_notes || []).length > 0 ? (
                <div className="space-y-2">
                  {showNotes.client_notes.map(n => (
                    <div key={n.id} className="p-3 bg-dark-800/50 rounded-lg">
                      <p className="text-white text-sm">{n.content}</p>
                      <div className="flex gap-3 mt-1 text-xs text-dark-400">
                        <span>By {n.created_by_name}</span>
                        <span>{new Date(n.created_at).toLocaleString()}</span>
                        {n.meeting_date && <span className="text-blue-400">Meeting: {n.meeting_date}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-dark-400 text-sm">No notes yet</p>}

              {/* Add note */}
              <form onSubmit={handleAddNote} className="space-y-3 pt-3 border-t border-dark-700">
                <textarea value={noteForm.content} onChange={e => setNoteForm({...noteForm, content: e.target.value})} placeholder="Add a note..." required className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm resize-none h-16" />
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs text-dark-400 mb-1">Meeting Date</label><input type="date" value={noteForm.meeting_date} onChange={e => setNoteForm({...noteForm, meeting_date: e.target.value})} className="w-full px-3 py-1.5 bg-dark-800 border border-dark-600 rounded text-white text-sm" /></div>
                  <div><label className="block text-xs text-dark-400 mb-1">Reminder (days)</label><input type="number" value={noteForm.reminder_days} onChange={e => setNoteForm({...noteForm, reminder_days: e.target.value})} placeholder="e.g. 3" className="w-full px-3 py-1.5 bg-dark-800 border border-dark-600 rounded text-white text-sm" /></div>
                </div>
                <button type="submit" className="w-full py-2 bg-primary-500 hover:bg-primary-600 text-dark-900 rounded-lg text-sm font-medium">Add Note</button>
              </form>
            </div>
          </div>
        </div>
      )}

    {/* Lead Detail Side Panel */}
    {showDetail && (
      <div className="fixed inset-0 z-50 flex justify-end">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowDetail(null)} />
        <div className="relative w-full max-w-md bg-dark-900 border-l border-dark-700 h-full overflow-y-auto p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">{showDetail.name}</h3>
            <button onClick={() => setShowDetail(null)} className="text-dark-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          {/* Status Badge */}
          <div className="flex flex-wrap gap-2">
            <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[showDetail.status] || ''}`}>{showDetail.status?.replace('_',' ')}</span>
            {showDetail.assigned_agent_id ? <span className="px-2 py-1 rounded text-xs bg-green-500/10 text-green-400">Assigned</span> : <span className="px-2 py-1 rounded text-xs bg-dark-600 text-dark-400">Unassigned</span>}
          </div>

          {/* Lead Info */}
          <div className="space-y-3">
            {[
              {label:'Phone', value: showDetail.phone},
              {label:'Email', value: showDetail.email},
              {label:'Source', value: showDetail.source},
              {label:'Budget', value: showDetail.budget},
              {label:'Property Interest', value: showDetail.property_interest},
              {label:'Notes', value: showDetail.notes},
            ].map(({label, value}) => value ? (
              <div key={label}>
                <p className="text-xs text-dark-400 mb-0.5">{label}</p>
                <p className="text-sm text-white">{value}</p>
              </div>
            ) : null)}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-dark-700">
            {showDetail.phone && <button onClick={() => { handleCall(showDetail.phone, showDetail.id); setShowDetail(null); }} className="flex items-center gap-1 px-3 py-2 bg-green-500/10 text-green-400 rounded-lg text-sm"><PhoneCall className="w-4 h-4" />Call</button>}
            <button onClick={() => { handleEdit(showDetail); setShowDetail(null); }} className="flex items-center gap-1 px-3 py-2 bg-blue-500/10 text-blue-400 rounded-lg text-sm"><Edit2 className="w-4 h-4" />Edit Details</button>
            <button onClick={() => { setShowNotes(showDetail); setShowDetail(null); }} className="flex items-center gap-1 px-3 py-2 bg-purple-500/10 text-purple-400 rounded-lg text-sm">Notes</button>
          </div>

          {/* Timeline */}
          <div className="pt-4 border-t border-dark-700">
            <p className="text-sm text-dark-300 mb-3 font-medium">Activity Timeline</p>
            {timelineLoading ? (
              <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div></div>
            ) : timeline?.events?.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {timeline.events.map((e, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${e.type === 'call' ? 'bg-green-400' : e.type === 'note' ? 'bg-purple-400' : 'bg-blue-400'}`} />
                    <div>
                      <p className="text-white text-xs">{e.label}</p>
                      <p className="text-dark-400 text-xs">{new Date(e.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-dark-500 text-xs">No activity yet</p>}
          </div>

          {/* Reassign Section */}
          <div className="pt-4 border-t border-dark-700">
            <p className="text-sm text-dark-300 mb-2 font-medium">Reassign Lead</p>
            {(isAdmin || user?.role === 'manager') ? (
              <div className="flex gap-2">
                <select
                  defaultValue={showDetail.assigned_agent_id || ''}
                  onChange={async (e) => {
                    try {
                      saveForUndo(showDetail);
                      await api.put(`/api/leads/${showDetail.id}`, { assigned_agent_id: e.target.value || null });
                      loadLeads();
                      setShowDetail(prev => ({...prev, assigned_agent_id: e.target.value}));
                    } catch { alert('Error reassigning lead'); }
                  }}
                  className="flex-1 px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm"
                >
                  <option value="">Unassigned</option>
                  {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            ) : (
              <div className="flex gap-2">
                <select disabled className="flex-1 px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-dark-500 text-sm opacity-50 cursor-not-allowed">
                  <option>{agents.find(a => a.id === showDetail.assigned_agent_id)?.name || 'Unassigned'}</option>
                </select>
                <p className="text-xs text-dark-500 mt-1">Only admins and managers can reassign leads</p>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </div>
  );
}
